import { AlertTriangle, BookOpen, Loader2 } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useContentPrograms } from '@/hooks/useContentPrograms';

interface Props {
  contentId: string | null | undefined;
}

/**
 * Shows the Rutas (programs) where a content_bank item is currently included.
 * If the item is not in any Ruta, renders a warning alert.
 * Renders nothing when contentId is null (e.g. creation mode).
 */
export function ContentProgramsBadge({ contentId }: Props) {
  const { data, isLoading } = useContentPrograms(contentId);

  if (!contentId) return null;

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-xs text-muted-foreground py-2">
        <Loader2 className="h-3 w-3 animate-spin" />
        Verificando Rutas asignadas...
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Alert variant="default" className="border-amber-500/40 bg-amber-500/10">
        <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
        <AlertTitle className="text-amber-700 dark:text-amber-300">
          Este contenido no está en ninguna Ruta
        </AlertTitle>
        <AlertDescription className="text-amber-700/90 dark:text-amber-300/90">
          No será visible para los participantes hasta que lo asignes a un capítulo de una Ruta
          desde el módulo de Rutas.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="rounded-md border border-border bg-muted/30 p-3">
      <div className="flex items-center gap-2 mb-2 text-sm font-medium">
        <BookOpen className="h-4 w-4 text-primary" />
        Rutas en las que está incluido ({data.length})
      </div>
      <TooltipProvider>
        <div className="flex flex-wrap gap-2">
          {data.map((p) => (
            <Tooltip key={p.program_id}>
              <TooltipTrigger asChild>
                <Badge variant="secondary" className="cursor-help">
                  {p.program_name}
                  <span className="ml-1 opacity-70">· {p.chapters.length}</span>
                </Badge>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-xs">
                <div className="text-xs font-semibold mb-1">Capítulos:</div>
                <ul className="text-xs space-y-0.5">
                  {p.chapters.map((c) => (
                    <li key={c.id}>• {c.name}</li>
                  ))}
                </ul>
              </TooltipContent>
            </Tooltip>
          ))}
        </div>
      </TooltipProvider>
    </div>
  );
}
